import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  LogIn, CheckCircle, ArrowRight, Shield, Users, 
  FileText, Palette, Mail, BarChart3, Building2,
  Phone, Zap, Star, Ruler, Calculator,
  Truck, Wrench, ClipboardList, Gem, Play
} from 'lucide-react';

// Custom Headstone Icon
const HeadstoneIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 22V10C4 6 7 3 12 3C17 3 20 6 20 10V22" />
    <path d="M4 22H20" />
    <path d="M8 22V18" />
    <path d="M16 22V18" />
    <path d="M12 8V14" />
    <path d="M9 11H15" />
  </svg>
);

// Custom Saw Blade Icon
const SawBladeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2L14 5L12 6L10 5L12 2Z" />
    <path d="M22 12L19 14L18 12L19 10L22 12Z" />
    <path d="M12 22L10 19L12 18L14 19L12 22Z" />
    <path d="M2 12L5 10L6 12L5 14L2 12Z" />
    <path d="M19.07 4.93L17.66 7.76L16.24 6.34L17.66 4.93L19.07 4.93Z" />
    <path d="M4.93 19.07L7.76 17.66L6.34 16.24L4.93 17.66L4.93 19.07Z" />
    <path d="M19.07 19.07L17.66 17.66L16.24 19.07L17.66 20.49L19.07 19.07Z" />
    <path d="M4.93 4.93L6.34 7.76L7.76 6.34L6.34 4.93L4.93 4.93Z" />
  </svg>
);

export default function SaaSLanding() {
  const navigate = useNavigate();
  const { login, API } = useContext(AuthContext);
  const currentYear = new Date().getFullYear();
  const [activeIndustry, setActiveIndustry] = useState('memorials'); // 'memorials' or 'worktops'
  const [demoLoading, setDemoLoading] = useState(false);
  
  const logoUrl = "https://customer-assets.emergentagent.com/job_e65197a9-8183-40fd-a17c-c3a01c1869d3/artifacts/0f0od067_Memorial%20Desk.webp";

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      // Log the demo access attempt
      await axios.post(`${API}/auth/demo-access-log`, { demo_type: activeIndustry }).catch(() => {});
      
      // Use different demo accounts based on industry
      const demoEmail = activeIndustry === 'worktops' ? 'demo-worktops@memorialdesk.co.uk' : 'Demo@memorialdesk.co.uk';
      
      const response = await axios.post(`${API}/auth/login`, {
        email: demoEmail,
        password: 'demo'
      });
      login(response.data.user, response.data.token);
      toast.success(`Welcome to the ${activeIndustry === 'worktops' ? 'Stone Fabricators' : 'Memorial'} demo!`);
      navigate(activeIndustry === 'worktops' ? '/worktops' : '/dashboard');
    } catch (error) {
      console.error('Demo login error:', error.response?.data);
      toast.error('Demo login failed. Please try again.');
    } finally {
      setDemoLoading(false);
    }
  };

  // Memorial-specific features
  const memorialFeatures = [
    {
      icon: FileText,
      title: "Quote & Invoice Management",
      description: "Create professional quotes and invoices in seconds. Track payments and send reminders automatically."
    },
    {
      icon: Palette,
      title: "Artwork Approval Workflow",
      description: "Upload artwork, send for client approval via email, and track revision requests all in one place."
    },
    {
      icon: Users,
      title: "Customer Database",
      description: "Keep all your client information organized. Access order history and contact details instantly."
    },
    {
      icon: Mail,
      title: "Automated Emails",
      description: "Send branded quotes, invoices, and artwork proofs directly to clients with one click."
    },
    {
      icon: BarChart3,
      title: "Order Tracking",
      description: "Track every order from quote to completion. Never lose track of where a job stands."
    },
    {
      icon: Building2,
      title: "White-Label Branding",
      description: "Your logo, your colors, your business. All client communications reflect your brand."
    }
  ];

  // Worktop-specific features
  const worktopFeatures = [
    {
      icon: Calculator,
      title: "Instant Pricing Calculator",
      description: "Calculate m² pricing, edge profiles, and extras automatically. Real-time cost breakdowns for every quote."
    },
    {
      icon: Ruler,
      title: "Piece Designer",
      description: "Design worktop pieces with precise dimensions, edge selections, and cut-out specifications."
    },
    {
      icon: Gem,
      title: "Material Management",
      description: "Track your stone inventory with supplier info, pricing tiers, and stock levels."
    },
    {
      icon: Truck,
      title: "Delivery & Fitting",
      description: "Manage templating, delivery schedules, and fitting appointments all in one place."
    },
    {
      icon: Wrench,
      title: "Maintenance Schedules",
      description: "Set up machine maintenance schedules with automated reminders and printable job sheets."
    },
    {
      icon: ClipboardList,
      title: "Job Sheet Generation",
      description: "Generate detailed workshop job sheets for each order with all specs and diagrams."
    }
  ];

  const features = activeIndustry === 'memorials' ? memorialFeatures : worktopFeatures;

  const memorialSteps = [
    { step: "1", title: "Create Order", description: "Enter client details and memorial requirements" },
    { step: "2", title: "Send Quote", description: "Generate and email a professional quote instantly" },
    { step: "3", title: "Artwork Approval", description: "Upload designs and get client approval via email" },
    { step: "4", title: "Complete & Invoice", description: "Mark complete and send final invoice" }
  ];

  const worktopSteps = [
    { step: "1", title: "Design Pieces", description: "Enter dimensions, edges, and cut-out requirements" },
    { step: "2", title: "Calculate Price", description: "Auto-calculate costs with your markup and extras" },
    { step: "3", title: "Send Quote", description: "Email professional quotes with material details" },
    { step: "4", title: "Generate Job Sheet", description: "Print workshop-ready specs for fabrication" }
  ];

  const steps = activeIndustry === 'memorials' ? memorialSteps : worktopSteps;

  const handleGetStarted = () => {
    navigate(`/login?tab=register&industry=${activeIndustry}`);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]"
         style={{backgroundImage: "radial-gradient(circle at 10% 10%, rgba(201,162,74,0.06) 0%, transparent 40%), radial-gradient(circle at 90% 90%, rgba(201,162,74,0.05) 0%, transparent 40%)"}}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/85 backdrop-blur-lg border-b border-[#c9a24a]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Memorial Desk" 
                className="h-14 sm:h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-slate-300 hover:text-[#c9a24a] hover:bg-[#c9a24a]/10"
              >
                Sign In
              </Button>
              <Button 
                onClick={handleGetStarted}
                className={`${activeIndustry === 'memorials' 
                  ? 'bg-[#c9a24a] hover:bg-[#d9b25a] text-black' 
                  : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white'
                } font-semibold tracking-wide`}
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-36 pb-20 px-4 relative overflow-hidden">
        {/* Background gradient orbs */}
        <div className={`absolute top-20 left-1/4 w-96 h-96 ${activeIndustry === 'memorials' ? 'bg-[#c9a24a]/10' : 'bg-amber-500/20'} rounded-full blur-3xl transition-colors duration-500`} />
        <div className={`absolute top-40 right-1/4 w-96 h-96 ${activeIndustry === 'memorials' ? 'bg-[#c9a24a]/8' : 'bg-orange-500/20'} rounded-full blur-3xl transition-colors duration-500`} />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          
          {/* Big brand logo for memorial visitors */}
          {activeIndustry === 'memorials' && (
            <div className="mb-8">
              <img src={logoUrl} alt="Memorial Desk" className="h-28 sm:h-36 lg:h-44 w-auto mx-auto" />
            </div>
          )}
          
          {/* Industry Toggle Switch */}
          <div className="mb-12">
            <div className="inline-flex items-center bg-[#111111] border border-[#c9a24a]/25 rounded-full p-1.5 backdrop-blur-sm">
              <button
                onClick={() => setActiveIndustry('memorials')}
                className={`relative px-6 sm:px-8 py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 ${
                  activeIndustry === 'memorials'
                    ? 'bg-[#c9a24a] text-black shadow-lg'
                    : 'text-slate-400 hover:text-[#c9a24a]'
                }`}
              >
                <span className="flex items-center gap-2">
                  <HeadstoneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  Memorial Masons
                </span>
              </button>
              <button
                onClick={() => setActiveIndustry('worktops')}
                className={`relative px-6 sm:px-8 py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 ${
                  activeIndustry === 'worktops'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-2">
                  <SawBladeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  Stone Fabricators
                </span>
              </button>
            </div>
          </div>

          {activeIndustry === 'memorials' && (
            <div className="text-xs sm:text-sm tracking-[0.4em] text-[#c9a24a] font-semibold mb-6">
              MANAGE. DESIGN. GROW.
            </div>
          )}

          <div className={`inline-flex items-center gap-2 bg-[#111111] border border-[#c9a24a]/20 rounded-full px-4 py-2 mb-8`}>
            <Zap className="w-4 h-4 text-[#c9a24a]" />
            <span className="text-sm text-slate-300">
              {activeIndustry === 'memorials' 
                ? 'Built by memorial masons, for memorial masons'
                : 'Built for granite, marble & quartz fabricators'
              }
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {activeIndustry === 'memorials' ? (
              <>
                Software for
                <br />
                <span className="text-[#c9a24a]">
                  memorial masons
                </span>
              </>
            ) : (
              <>
                Manage your stone
                <br />
                <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                  fabrication workshop
                </span>
              </>
            )}
          </h1>

          {activeIndustry === 'memorials' && (
            <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-[#c9a24a] to-transparent mx-auto mb-6" />
          )}
          
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            {activeIndustry === 'memorials' 
              ? 'The all-in-one platform for memorial masons. Manage quotes, artwork approvals, invoices, and customer relationships from one beautiful dashboard.'
              : 'The complete solution for granite, marble and quartz worktop fabricators. Handle quotes, job sheets, material tracking, and machine maintenance - all in one powerful platform.'
            }
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              size="lg"
              onClick={handleGetStarted}
              className={`${activeIndustry === 'memorials'
                ? 'bg-[#c9a24a] hover:bg-[#d9b25a] text-black'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white'
              } text-lg px-8 py-6 h-auto font-semibold tracking-wide`}
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={handleDemoLogin}
              disabled={demoLoading}
              data-testid={`demo-btn-${activeIndustry}`}
              className={`${activeIndustry === 'memorials'
                ? 'border-[#c9a24a] text-[#c9a24a] hover:bg-[#c9a24a]/10 bg-transparent'
                : 'border-orange-500 text-orange-400 hover:bg-orange-500/10'
              } text-lg px-8 py-6 h-auto`}
            >
              <Play className="w-5 h-5 mr-2" />
              {demoLoading ? 'Loading Demo...' : 'Try Live Demo'}
            </Button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {[
              { value: "30", label: "Day Free Trial" },
              { value: "from £18", label: "Per Month" },
              { value: "Direct", label: "UK Support" },
              { value: "100%", label: "UK Based" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-[#0e0e0e]/60 border-y border-[#c9a24a]/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {activeIndustry === 'memorials'
                ? 'Everything you need to manage your memorial business'
                : 'Everything you need to run your fabrication workshop'
              }
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {activeIndustry === 'memorials'
                ? 'From initial quote to final delivery, Memorial Desk keeps everything organized and professional.'
                : 'From customer enquiry to workshop floor, streamline every step of your stone fabrication business.'
              }
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="bg-[#111111] border-[#c9a24a]/20 hover:border-[#c9a24a]/50 transition-colors">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 ${activeIndustry === 'memorials' 
                    ? 'bg-[#c9a24a]/15 ring-1 ring-[#c9a24a]/40' 
                    : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
                  } rounded-xl flex items-center justify-center mb-4`}>
                    <feature.icon className={`w-6 h-6 ${activeIndustry === 'memorials' ? 'text-[#c9a24a]' : 'text-amber-400'}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple workflow, powerful results
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((item, i) => (
              <div key={i} className="text-center">
                <div className={`w-16 h-16 ${activeIndustry === 'memorials'
                  ? 'bg-[#c9a24a] text-black'
                  : 'bg-gradient-to-br from-amber-500 to-orange-600'
                } rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold`}>
                  <span className={activeIndustry === 'memorials' ? 'text-black' : 'text-white'}>{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-[#0e0e0e]/60 border-y border-[#c9a24a]/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-400">
              Start free for 30 days. No credit card required.
            </p>
          </div>
          
          {/* Memorial pricing - 3 tiers */}
          {activeIndustry === 'memorials' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                {
                  name: "Basic",
                  price: "£18",
                  period: "/month",
                  description: "Essential memorial management",
                  features: [
                    "Unlimited orders & quotes",
                    "Email notifications",
                    "Customer database",
                    "Artwork approval workflow",
                    "Order tracking",
                    "Stock management"
                  ],
                  highlighted: false
                },
                {
                  name: "Standard",
                  price: "£25",
                  period: "/month",
                  description: "Professional with white-labeling",
                  features: [
                    "Everything in Basic",
                    "Your logo on documents",
                    "Branded customer emails",
                    "Custom email footer",
                    "Remove Memorial Desk branding",
                    "Priority support"
                  ],
                  highlighted: true
                },
                {
                  name: "Premium",
                  price: "£35",
                  period: "/month",
                  description: "Full access with worktops",
                  features: [
                    "Everything in Standard",
                    "Team chat & messaging",
                    "Worktop management",
                    "Worktop quotes & invoices",
                    "Material calculator",
                    "Edge profile pricing",
                    "Multi-industry support"
                  ],
                  highlighted: false
                }
              ].map((plan, i) => (
                <Card key={i} className={`${plan.highlighted 
                  ? 'border-2 border-[#c9a24a] scale-105 shadow-2xl shadow-[#c9a24a]/10' 
                  : 'border-[#c9a24a]/20'
                } bg-[#111111]`}>
                  <CardContent className="p-6">
                    {plan.highlighted && (
                      <div className="inline-block bg-[#c9a24a]/15 text-[#c9a24a] text-xs font-semibold px-3 py-1 rounded-full mb-4 ring-1 ring-[#c9a24a]/40">
                        MOST POPULAR
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-white">{plan.price}</span>
                      <span className="text-slate-400">{plan.period}</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-2 text-slate-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-[#c9a24a]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={handleGetStarted}
                      className={`w-full font-semibold tracking-wide ${plan.highlighted 
                        ? 'bg-[#c9a24a] hover:bg-[#d9b25a] text-black'
                        : 'bg-[#1a1a1a] hover:bg-[#242424] text-slate-200 border border-[#c9a24a]/20'
                      }`}
                    >
                      Start Free Trial
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Worktop pricing - 2 tiers */}
          {activeIndustry === 'worktops' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {[
                {
                  name: "Standard",
                  price: "£18",
                  period: "/month",
                  description: "Everything you need to get started",
                  features: [
                    "Unlimited orders & quotes",
                    "Email notifications",
                    "Customer database",
                    "Job sheet generation",
                    "Material management",
                    "Maintenance schedules"
                  ],
                  highlighted: false
                },
                {
                  name: "White Label",
                  price: "£25",
                  period: "/month",
                  description: "Your brand, your business",
                  features: [
                    "Everything in Standard",
                    "Team chat & messaging",
                    "Your logo on all documents",
                    "Branded customer emails",
                    "Custom email footer",
                    "Remove Memorial Desk branding",
                    "Priority support"
                  ],
                  highlighted: true
                }
              ].map((plan, i) => (
                <Card key={i} className={`${plan.highlighted 
                  ? 'border-2 border-orange-500' 
                  : 'border-slate-700'
                } bg-slate-800/50`}>
                  <CardContent className="p-8">
                    {plan.highlighted && (
                      <div className="inline-block bg-orange-500/20 text-orange-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                        MOST POPULAR
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-white">{plan.price}</span>
                      <span className="text-slate-400">{plan.period}</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-center gap-2 text-slate-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-amber-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={handleGetStarted}
                      className={`w-full ${plan.highlighted 
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
                        : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      Start Free Trial
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1,2,3,4,5].map((_, i) => (
              <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            {activeIndustry === 'memorials'
              ? 'Trusted by memorial businesses across the UK'
              : 'Trusted by stone fabricators across the UK'
            }
          </h2>
          <p className="text-slate-400 mb-8">
            {activeIndustry === 'memorials'
              ? 'Join memorial masons who\'ve streamlined their operations with Memorial Desk.'
              : 'Join granite, marble and quartz fabricators who\'ve transformed their workshop efficiency.'
            }
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
            <div className="flex items-center gap-2 text-slate-400">
              <Shield className="w-5 h-5" />
              <span className="text-sm">Secure & Private</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Phone className="w-5 h-5" />
              <span className="text-sm">Phone & Remote Support</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Users className="w-5 h-5" />
              <span className="text-sm">Built-in Bug Reporting</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 px-4 ${activeIndustry === 'memorials' 
        ? 'bg-gradient-to-r from-[#c9a24a]/10 via-[#c9a24a]/5 to-[#c9a24a]/10 border-y border-[#c9a24a]/25'
        : 'bg-gradient-to-r from-amber-500/10 to-orange-500/10'
      }`}>
        <div className="max-w-4xl mx-auto text-center">
          {activeIndustry === 'memorials' && (
            <div className="text-xs tracking-[0.4em] text-[#c9a24a] font-semibold mb-4">
              TRY IT FREE FOR 30 DAYS
            </div>
          )}
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {activeIndustry === 'memorials'
              ? 'Ready to streamline your memorial business?'
              : 'Ready to transform your stone fabrication workshop?'
            }
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Start your 30-day free trial today. No credit card required.
          </p>
          <Button 
            size="lg"
            onClick={handleGetStarted}
            className={`${activeIndustry === 'memorials'
              ? 'bg-[#c9a24a] hover:bg-[#d9b25a] text-black'
              : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white'
            } text-lg px-8 py-6 h-auto font-semibold tracking-wide`}
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-[#c9a24a]/15">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="Memorial Desk" 
                className="h-12 w-auto"
              />
              <span className="text-slate-400 text-sm">
                {activeIndustry === 'memorials' 
                  ? 'Memorial business management made simple'
                  : 'Stone fabrication management made simple'
                }
              </span>
            </div>
            <div className="text-slate-500 text-sm">
              © {currentYear} Memorial Desk. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
